// /groups/:groupId/photos — the group gallery.
//
// One upload form, one tile grid, and the moderation queue folded into the same
// page rather than hidden behind a separate screen. A moderator sees pending
// photos inline with a badge, because a queue you have to remember to visit is a
// queue that does not get emptied.
//
// The client-side size check exists to give an instant answer, not to enforce
// anything — the server checks again, and its answer is the one that counts.

import {
  ApiClientError,
  approvePhoto,
  deletePhoto,
  fetchGroup,
  listPhotos,
  updatePhotoCaption,
  uploadPhoto,
} from '../api/client.js';
import { navigate, type View } from '../router.js';
import { el } from '../dom.js';
import { groupRunsFasts, groupShowsLeaderboard, groupSubnav } from './groupNav.js';
import {
  CAPTION_MAX,
  fmtInstant,
  GroupRole,
  PHOTO_ACCEPT,
  TimeFormat,
  type GroupDetail,
  type Photo,
} from '@msc/shared';
import { viewerZone } from '../viewerZone.js';
import { openLightbox } from './lightbox.js';
import { blockButton, reportButton } from './reportControl.js';

/** Matches the server's UPLOAD_MAX_BYTES default. The server is authoritative. */
const MAX_BYTES = 10 * 1024 * 1024;

export const photosView: View = async (outlet, ctx) => {
  const groupId = Number(ctx.params.groupId);
  const main = el('main', { class: 'page' }, [el('p', { class: 'muted' }, 'Loading')]);
  outlet.append(main);

  if (!Number.isInteger(groupId) || groupId <= 0) {
    main.replaceChildren(el('p', { class: 'form-error' }, 'Invalid group.'));
    return;
  }

  let group: GroupDetail;
  let zone: string;
  try {
    [group, zone] = await Promise.all([fetchGroup(groupId), viewerZone()]);
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 401) {
      navigate('/login', true);
      return;
    }
    main.replaceChildren(
      el(
        'p',
        { class: 'form-error' },
        err instanceof ApiClientError && err.status === 403
          ? 'You are not a member of this group.'
          : 'Could not load this group.',
      ),
    );
    return;
  }

  const grid = el('div', { class: 'photo-grid' });
  const pendingNote = el('p', { class: 'muted small' });

  async function reload(): Promise<void> {
    try {
      const data = await listPhotos(groupId, { limit: 200 });
      pendingNote.textContent =
        data.pendingCount > 0
          ? `${data.pendingCount} photo${data.pendingCount === 1 ? '' : 's'} awaiting approval.`
          : '';
      grid.replaceChildren(
        ...(data.photos.length
          ? data.photos.map((p, i) => tile(p, data.photos, i, zone, reload))
          : [el('p', { class: 'muted' }, 'No photos yet.')]),
      );
    } catch {
      grid.replaceChildren(el('p', { class: 'form-error' }, 'Could not load the photos.'));
    }
  }

  main.replaceChildren(
    el('p', {}, [el('a', { href: `/groups/${groupId}` }, `Back to ${group.name}`)]),
    el('div', { class: 'page-head' }, [el('h1', {}, `${group.name} photos`)]),
    groupSubnav(groupId, 'photos', group.myRole >= GroupRole.ADMIN, groupRunsFasts(group.allowedEventTypes), groupShowsLeaderboard(group.allowedEventTypes)),
    uploadCard(group, reload),
    pendingNote,
    grid,
  );

  await reload();
};

// --- Upload ---------------------------------------------------------------

function uploadCard(group: GroupDetail, onDone: () => Promise<void>): HTMLElement {
  const error = el('p', { class: 'form-error', hidden: true });
  const ok = el('p', { class: 'form-ok', hidden: true });
  const submit = el('button', { type: 'submit', class: 'btn' }, 'Upload');

  const file = el('input', {
    id: 'photo-file',
    name: 'photo',
    type: 'file',
    // Kept in step with the server's allowlist via the shared constant. A hint
    // to the file picker only; content sniffing is what actually decides.
    accept: PHOTO_ACCEPT,
    required: true,
  }) as HTMLInputElement;

  const caption = el('input', {
    id: 'photo-caption',
    name: 'caption',
    type: 'text',
    maxlength: CAPTION_MAX,
    placeholder: 'Optional caption',
  }) as HTMLInputElement;

  const form = el(
    'form',
    { method: 'post', action: `/groups/${group.groupId}/photos`, novalidate: true },
    [
      el('label', { for: 'photo-file' }, 'Photo'),
      file,
      el('label', { for: 'photo-caption' }, 'Caption'),
      caption,
      el('p', { class: 'muted small' }, 'JPEG, PNG, WebP, or GIF. Up to 10 MB.'),
      error,
      ok,
      submit,
    ],
  ) as HTMLFormElement;

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    error.hidden = true;
    ok.hidden = true;

    const chosen = file.files?.[0];
    if (!chosen) {
      error.textContent = 'Please choose a photo to upload.';
      error.hidden = false;
      return;
    }
    // An instant answer for the obvious case. The server checks again, and its
    // check is the one that matters — this just saves pushing 40 MB up a phone
    // connection to be told no.
    if (chosen.size > MAX_BYTES) {
      error.textContent = 'That photo is too large. Please choose an image under 10 MB.';
      error.hidden = false;
      return;
    }

    submit.setAttribute('disabled', 'disabled');
    submit.textContent = 'Uploading';
    try {
      const result = await uploadPhoto(group.groupId, chosen, {
        caption: caption.value.trim() || undefined,
      });
      ok.textContent = result.pending
        ? 'Uploaded. A moderator will approve it before others can see it.'
        : 'Uploaded.';
      ok.hidden = false;
      form.reset();
      await onDone();
    } catch (err) {
      // Branch on the code, so each distinct failure gets its own sentence
      // rather than the misleading catch-all the PHP version shipped.
      const code = err instanceof ApiClientError ? err.code : null;
      error.textContent =
        code === 'FILE_TOO_LARGE'
          ? 'That photo is too large. Please choose an image under 10 MB.'
          : code === 'UNSUPPORTED_MEDIA'
            ? 'Only JPEG, PNG, WebP, and GIF images are allowed.'
            : code === 'NO_FILE'
              ? 'Please choose a photo to upload.'
              : code === 'FORBIDDEN'
                ? 'You do not have permission to post photos in this group.'
                : err instanceof ApiClientError
                  ? err.message
                  : 'Upload failed. Please try again.';
      error.hidden = false;
    } finally {
      submit.removeAttribute('disabled');
      submit.textContent = 'Upload';
    }
  });

  const canPost =
    group.postPermission === 'members_open' ||
    (group.postPermission === 'admins_only' && group.myRole >= GroupRole.ADMIN) ||
    (group.postPermission === 'owner_only' && group.myRole >= GroupRole.OWNER);

  if (!canPost) {
    return el('section', { class: 'card' }, [
      el('h2', {}, 'Add a photo'),
      el(
        'p',
        { class: 'muted' },
        'Only group admins can post in this group. You can still browse the gallery.',
      ),
    ]);
  }

  return el('section', { class: 'card' }, [el('h2', {}, 'Add a photo'), form]);
}

// --- Tiles ---------------------------------------------------------------

function tile(
  photo: Photo,
  all: Photo[],
  position: number,
  viewerZone: string,
  onChange: () => Promise<void>,
): HTMLElement {
  const img = el('img', {
    class: 'photo-thumb',
    src: photo.url,
    alt: photo.caption || `Photo by ${photo.uploaderName}`,
    loading: 'lazy',
  });
  const open = el('button', { class: 'photo-open', type: 'button' }, [img]);
  open.addEventListener('click', () => openLightbox(all, position));

  const meta = el('p', { class: 'muted small' }, [
    photo.uploaderName,
    ' · ',
    fmtInstant(photo.createdAt, viewerZone, TimeFormat.INSTANT),
  ]);

  const captionLine = el('p', { class: 'photo-caption' }, photo.caption || '');
  const actions = el('div', { class: 'photo-actions' });

  const children: HTMLElement[] = [open];
  if (!photo.approved) {
    children.push(el('span', { class: 'badge badge-pending' }, 'Pending'));
  }
  children.push(captionLine, meta, actions);

  if (photo.canApprove && !photo.approved) {
    const btn = el('button', { class: 'btn btn-link', type: 'button' }, 'Approve');
    btn.addEventListener('click', async () => {
      btn.setAttribute('disabled', 'disabled');
      try {
        await approvePhoto(photo.photoId);
        await onChange();
      } catch (err) {
        window.alert(err instanceof ApiClientError ? err.message : 'Could not approve that photo.');
        btn.removeAttribute('disabled');
      }
    });
    actions.append(btn);
  }

  if (photo.canEditCaption) {
    const btn = el('button', { class: 'btn btn-link', type: 'button' }, 'Caption');
    btn.addEventListener('click', async () => {
      const next = window.prompt('Caption (leave blank to clear)', photo.caption);
      if (next === null) return;
      try {
        await updatePhotoCaption(photo.photoId, next.trim());
        await onChange();
      } catch (err) {
        window.alert(err instanceof ApiClientError ? err.message : 'Could not save that caption.');
      }
    });
    actions.append(btn);
  }

  if (photo.canDelete) {
    const btn = el('button', { class: 'btn btn-link btn-danger', type: 'button' }, 'Delete');
    btn.addEventListener('click', async () => {
      if (!window.confirm('Delete this photo?')) return;
      btn.setAttribute('disabled', 'disabled');
      try {
        await deletePhoto(photo.photoId);
        await onChange();
      } catch (err) {
        window.alert(err instanceof ApiClientError ? err.message : 'Could not delete that photo.');
        btn.removeAttribute('disabled');
      }
    });
    actions.append(btn);
  }

  // Guideline 1.2. `photo.mine` was added for this: canDelete is also true for a
  // group admin looking at someone else's photo, so it cannot stand in.
  const report = reportButton('photo', photo.photoId, photo.mine);
  if (report) actions.append(report);
  const block = blockButton(photo.userId, photo.uploaderName, photo.mine);
  if (block) actions.append(block);

  return el(
    'figure',
    { class: `photo-tile${photo.approved ? '' : ' photo-pending'}`, 'data-author': photo.userId },
    children,
  );
}
